/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { useCallback, useEffect, useState } from 'react';

import { DimensionScaler } from '../../../../core/device/DimensionScaler';
import { OneLineGuideCell } from '../../../../core/entities';
import { RootState, useTivoSelector } from '../../../../core/tivo/tivo-redux-wrapper';
import { DateTime } from '../../../../core/utils';

import './TimebarOneLineGuide.scss';

interface TimebarOneLineGuideProps {
    startTime: DateTime;
    duration: number;
    cellWidth: number;
}

export const TimebarOneLineGuidePlaceholder: React.FC = (): JSX.Element => (
    <div className="olg-timebar-container"></div>
);

const ONE_LINE_GUIDE_REFRESH_PERIOD = 1 * 60 * 1000; // 1 minute
const MILLISECONDS_IN_A_SECOND = 1000;
const HEAD_WIDTH = 22;
const HEAD_OFFSET = 3;
const GAP_BETWEEN_TILES = 5;

const TimebarOneLineGuide: React.FC<TimebarOneLineGuideProps> = ({
    startTime,
    duration,
    cellWidth,
}): JSX.Element => {
    const isFutureMode = useTivoSelector((state: RootState) => state.oneLineGuide.isFutureMode);
    const { offerId: currentSelectedCellOfferId }: OneLineGuideCell =
        useTivoSelector((state: RootState) => state.oneLineGuide.currentSelectedCell) || {};
    const { offerId: currentAiringCellOfferId }: OneLineGuideCell =
        useTivoSelector((state: RootState) => state.oneLineGuide.currentAiringCell) || {};

    const pixelsPerSeconds = cellWidth / duration;
    const isSelectedCellLive = currentSelectedCellOfferId === currentAiringCellOfferId;
    const isSelectedCellInFuture = startTime.getTime() > Date.now();

    const getElapsedSeconds = useCallback(
        (elapsedFrom: DateTime) => (Date.now() - elapsedFrom.getTime()) / MILLISECONDS_IN_A_SECOND,
        [],
    );

    const [width, setWidth] = useState(getElapsedSeconds(startTime) * pixelsPerSeconds);

    // Periodically update the time bar head
    useEffect(() => {
        if (!isFutureMode) {
            return () => {};
        }

        // From the width representing the time passed we need to subtract half of the head width
        // (so the head is centered on the time to indicate) but not the part of it that
        // overlaps the bar
        const headSize = HEAD_WIDTH / 2 - HEAD_OFFSET;
        const intervalId = setInterval(() => {
            setWidth(
                getElapsedSeconds(startTime) * pixelsPerSeconds +
                    (isSelectedCellLive ? -headSize : GAP_BETWEEN_TILES - headSize),
            );
        }, ONE_LINE_GUIDE_REFRESH_PERIOD);

        // Setting the width outside the interval because if not,
        // when the user moves the highlight away from the currently airing show
        // and then comes back, the time bar will have the last computed size for a period
        // equal to ONE_LINE_GUIDE_REFRESH_PERIOD
        setWidth(
            getElapsedSeconds(startTime) * pixelsPerSeconds +
                (isSelectedCellLive ? -headSize : GAP_BETWEEN_TILES - headSize),
        );

        return () => clearInterval(intervalId);
    }, [getElapsedSeconds, isFutureMode, isSelectedCellLive, pixelsPerSeconds, startTime]);

    return (
        <div className="olg-timebar-container">
            {!isFutureMode ? (
                <div className="olg-timebar-past"></div>
            ) : (
                !isSelectedCellInFuture && (
                    <>
                        <div
                            className="olg-timebar"
                            style={{
                                width: `${width > 0 ? DimensionScaler.getInstance().getScaledWidth(width) : 0}px`,
                            }}
                        ></div>
                        <span className="olg-timebar-head"></span>
                    </>
                )
            )}
        </div>
    );
};

export default TimebarOneLineGuide;
